import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Trash2, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useCart } from '@/context/CartContext';

function BagIconWithBadge() {
  const { totalCount } = useCart();
  return (
    <>
      <ShoppingBag className="h-5 w-5" />
      {totalCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center">
          {totalCount > 99 ? '99+' : totalCount}
        </span>
      )}
    </>
  );
}

export default function CartSheet() {
  const [open, setOpen] = useState(false);
  const { items, removeItem, updateQuantity } = useCart();
  const navigate = useNavigate();

  const goToInquiry = () => {
    setOpen(false);
    navigate('/', { state: { scrollToContact: true } });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative hidden md:inline-flex border-border/60 bg-background/80 shrink-0"
          aria-label="Korpa"
        >
          <BagIconWithBadge />
        </Button>
      </SheetTrigger>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative inline-flex md:hidden border-border/60 bg-background/80 shrink-0"
          aria-label="Korpa"
        >
          <BagIconWithBadge />
        </Button>
      </SheetTrigger>

      <SheetContent className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle>Moji artikli</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Korpa je prazna. Dodajte proizvode sa stranice kategorije.
            </p>
          ) : (
            items.map((item) => (
              <div
                key={item.productId}
                className="flex gap-3 border border-border/40 rounded-lg p-3 bg-card"
              >
                <div className="w-16 h-16 rounded-md overflow-hidden bg-gallery-card shrink-0 border border-black/[0.06]">
                  <img src={item.image} alt="" className="w-full h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground line-clamp-2">{item.name}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-sm w-8 text-center tabular-nums">{item.quantity}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item, item.quantity + 1)}
                      disabled={item.quantity >= 99}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 ml-auto text-destructive hover:text-destructive"
                      onClick={() => removeItem(item)}
                      aria-label="Ukloni"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border pt-4 space-y-2">
            <Button className="w-full bg-primary hover:bg-primary/90" onClick={goToInquiry}>
              Kreiraj upit
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Otvoriće se kontakt forma. Proizvodi iz korpe biće priloženi upitu.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
